/**
 * Detect that a newer frontend build has been deployed while this tab was open.
 *
 * Why this exists
 * ---------------
 * nginx serves index.html with `no-cache`, so *reloading* always fetches the
 * newest bundle. But a tab that is simply left open never reloads — it keeps
 * running the JavaScript it loaded hours ago. During an active deploy/test
 * cycle that produces a silent and very misleading failure mode: a fix is
 * built, pushed and rolled out, the pod restarts, and the next test run still
 * exhibits the old behaviour because the browser is still executing the old
 * bundle. There is nothing on screen to distinguish "the fix does not work"
 * from "you are not running the fix".
 *
 * This polls index.html for the hashed bundle name and, when it differs from
 * the bundle this page actually loaded, says so and offers a reload.
 */

import React, { useEffect, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';

const POLL_INTERVAL_MS = 60_000;
const ASSET_RE = /assets\/(index-[A-Za-z0-9_-]+\.js)/;

/** The bundle filename this page is currently executing. */
const currentBundle = (): string | null => {
  // Vite emits ESM, so import.meta.url is this module's own hashed URL.
  const fromSelf = import.meta.url.match(ASSET_RE);
  if (fromSelf) return fromSelf[1];
  // Fallback for any non-module script tag.
  const tag = document.querySelector<HTMLScriptElement>('script[src*="assets/index-"]');
  return tag?.src.match(ASSET_RE)?.[1] ?? null;
};

const BuildUpdateNotice: React.FC = () => {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const mine = currentBundle();
    // Dev server (no hashed bundle) — nothing to compare against.
    if (!mine) return;

    // Make the running build visible without opening the network tab, so a
    // screenshot is enough to tell which code produced the behaviour.
    // eslint-disable-next-line no-console
    console.info(`[QA Agent] running bundle: ${mine}`);
    (window as any).__QA_AGENT_BUNDLE__ = mine;

    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const deployed = (await res.text()).match(ASSET_RE)?.[1];
        if (!cancelled && deployed && deployed !== mine) setStale(true);
      } catch {
        // Offline or nginx restarting — not worth surfacing.
      }
    };

    const timer = window.setInterval(check, POLL_INTERVAL_MS);
    check();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  return (
    <Snackbar
      open={stale}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity="info"
        variant="filled"
        action={
          <Button color="inherit" size="small" onClick={() => window.location.reload()}>
            Reload
          </Button>
        }
      >
        A newer version of the QA Agent has been deployed. This tab is still
        running the previous one.
      </Alert>
    </Snackbar>
  );
};

export default BuildUpdateNotice;
